import { Sparkles, X, MoreVertical, Maximize2, Minimize2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useState, useRef, useEffect } from 'react';
import { ConfirmModal } from './ConfirmModal';
import { trackUiButtonEventAsync, UI_BUTTON_EVENTS } from '../../../../services/api/uiButtonStatsService';
import { getTenantName } from '../../../../utils/brandingStyles';

interface HeaderProps {
    userName: string;
    setShoRightPanel: (show: boolean) => void;
    onDeleteAnalysis: () => void;
    onNewAnalysis: () => void;
    onOpenReports: () => void;
    isMaximized?: boolean;
    onToggleMaximize?: () => void;
}

export const Header = ({ userName, setShoRightPanel, onDeleteAnalysis, onNewAnalysis, onOpenReports, isMaximized, onToggleMaximize }: HeaderProps) => {
    const { t } = useTranslation();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <>
            <div className="border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-1 p-1">
                    <div className="">
                        <Sparkles className="p-0 pt-1" size={22} />
                    </div>
                    <h2 className="text-sm font-semibold">
                        <span className="text-xl">{getTenantName()}</span>
                    </h2>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative" ref={dropdownRef}>
                        <button
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            title={t('words.menu')}
                        >
                            <MoreVertical size={18} className="text-gray-600" />
                        </button>
                        <button
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                            onClick={() => {
                                void trackUiButtonEventAsync(
                                    isMaximized
                                        ? UI_BUTTON_EVENTS.mapping.analysisMinimize
                                        : UI_BUTTON_EVENTS.mapping.analysisMaximize
                                );
                                onToggleMaximize?.();
                            }}
                            title={isMaximized ? "Réduire" : "Agrandir"}
                        >
                            {isMaximized ? (
                                <Minimize2 size={18} className="text-gray-600" />
                            ) : (
                                <Maximize2 size={18} className="text-gray-600" />
                            )}
                        </button>

                        {isDropdownOpen && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-50">
                                <button
                                    onClick={() => {
                                        setIsDropdownOpen(false);
                                        void trackUiButtonEventAsync(UI_BUTTON_EVENTS.mapping.analysisOpenSavedReports);
                                        onOpenReports();
                                    }}
                                    className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                    {t('features.swimlane.open')}
                                </button>
                                <button
                                    onClick={() => {
                                        setIsDropdownOpen(false);
                                        void trackUiButtonEventAsync(UI_BUTTON_EVENTS.mapping.analysisNewAnalysis);
                                        onNewAnalysis();
                                    }}
                                    className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                    {t('features.swimlane.newAnalysis')}
                                </button>
                                {/* <button
                                    onClick={() => {
                                        setIsDropdownOpen(false);
                                        setIsConfirmModalOpen(true);
                                    }}
                                    className="w-full px-4 py-2 text-left text-red-600 hover:bg-gray-50 transition-colors"
                                >
                                    {t('words.delete')}
                                </button> */}
                            </div>
                        )}
                    </div>
                    <button
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        onClick={() => {
                            void trackUiButtonEventAsync(UI_BUTTON_EVENTS.mapping.analysisClosePanel);
                            setShoRightPanel(false);
                        }}
                    >
                        <X size={18} className="text-gray-600" />
                    </button>
                </div>
            </div>

            <div className="p-1 pb-2 border-b border-gray-100">
                <h2 className="text-xl font-semibold text-gray-900 text-gradient">
                    {t("words.hello")}, {userName}
                </h2>
                <p className="text-md text-gray-500">
                    {t('features.swimlane.reportGenerate')}
                </p>
            </div>

            <ConfirmModal
                isOpen={isConfirmModalOpen}
                onClose={() => setIsConfirmModalOpen(false)}
                onConfirm={onDeleteAnalysis}
                title={t('features.swimlane.deleteAnalysis')}
                message={t('features.swimlane.deleteAnalysisConfirm')}
            />
        </>
    );
};